import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { LinearProgress } from "@mui/material";
import { Box, Divider,Grid, Typography } from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro-6";
import React from "react";
import { useTranslation } from "react-i18next";

import AppliedFilters from "../../../components/filters/applied-filters";
import { useDataGridLocaleText } from "../../../design/use-datagrid-locale-text";
import useTableHeight from "../../../utils/use-table-height";
import { TimeOffBalance } from "../store/time-off-balances-report-types";

interface TimeOffBalancesReportTableProps {
	rows: TimeOffBalance[];
	initialData: TimeOffBalance[];
	getReportStatus: string;
	columns: GridColDef[];
	open?: boolean;
  canUpdateLeaveBalance: boolean;
}

function NoDataAvailable() {
	// This component shows when the initial API call returns no data - No data from backend
	const { t } = useTranslation();
	return (
		<Box
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Box display='flex' flexDirection='column' alignItems='center' gap={1}>
				<CalendarTodayIcon fontSize='large' />
				<Typography>
					{t("time-off-balances_no-data-message")}
				</Typography>
			</Box>
		</Box>
	);
}

function NoResultsFromFilters() {
	// This component shows when filters result in no data - Data from backend but no data after filtering
    const { t } = useTranslation();
    return (
        <Box
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Box display='flex' flexDirection='column' alignItems='center' gap={1}>
				<CalendarTodayIcon fontSize='large' />
				<Typography>
					{t("time-off-balances_no-balances-found")}
				</Typography>
			</Box>
		</Box>
    );
}

const TimeOffBalancesReportTable = ({
	rows,initialData, getReportStatus, columns, open,canUpdateLeaveBalance
}: TimeOffBalancesReportTableProps) => {
	const { boxRef } = useTableHeight();
	const dataGridLocaleText = useDataGridLocaleText();
	const NoRowsOverlay = () => {
        if (getReportStatus === "fulfilled" && initialData.length === 0) {
            return <NoDataAvailable />;
        }
        return <NoResultsFromFilters />;
    };
	return (
		<Box sx={{ flexGrow: 1, overflow: "hidden" }}>
            <Grid container wrap='nowrap' sx={{ position: "relative", flex: 1, height: "100%" }}>
				{/* This is the main table content */}
				<Grid item xs sx={{ transition: "padding-left 0.3s ease", 
					pl: 0, 
					display: "flex",
                    flexDirection: "column",
					flex: 1,
					}}>
						<Box>
							<Divider sx={{ mt: 0 }} />
							<AppliedFilters />
						</Box>
					<Box
					ref={boxRef}
					sx={{
						height: "calc(100vh - 280px)",
						width: "100%",
						minHeight: "100px",
				}}
					>
						<DataGridPro
							rows={rows}
							columns={columns}
							localeText={dataGridLocaleText}
							loading={getReportStatus === "pending"}
							pagination
							disableRowSelectionOnClick
							disableColumnSelector
							disableDensitySelector
							disableColumnReorder
							disableColumnMenu
              pinnedColumns={{right: ["actions"]}}
              scrollbarSize={6}
							slots={{
								loadingOverlay: LinearProgress,
								noRowsOverlay: NoRowsOverlay,
							}}
							initialState={{
								pagination: { paginationModel: { pageSize: 25 } },
							}}
							pageSizeOptions={[25, 50, 100]}
							sx={{
								flex: 1,
								height: "100%",
                overflow: "hidden",
								border: "none !important",
								"& .MuiDataGrid-columnHeader": {
									"&:first-of-type": {
										paddingLeft: "24px",
									},
                  "&.hover-column-header": {
                    "& .MuiDataGrid-columnSeparator": {
                      display: "none",
                    },
                    "& .MuiDataGrid-sortIcon": {
                      display: "none",
                    },
                  },
                  "& .MuiDataGrid-columnSeparator": {
                    display: "none",
                  },
								},
								"& .MuiDataGrid-cell": {
									"&:first-of-type": {
										paddingLeft: "24px",
									},
								},
                "& .actions-col-header": {
                  paddingLeft: "10px !important",
                  paddingRight: "10px !important",
                },
                "& .actions-col-cell": {
                  paddingLeft: "10px !important",
                  paddingRight: "10px !important",
                },
                "& .MuiTablePagination-root": {
                  marginRight: "60px",
                },
                ".MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                ".MuiDataGrid-columnHeader:hover": {
                  "& .MuiDataGrid-columnSeparator": {
                    display: "flex",
                  },
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                  outline: "none !important",
                },
							}}
						/>
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};

export default TimeOffBalancesReportTable;